/**
 * What the AI is shown of a chat attachment (#1291 / #1292) — ONE definition for
 * the server that builds the model's context (`features/agent/core/convert-messages.ts`)
 * and the composer that tells the user, before sending, when a file will be
 * attached but not seen (`features/agent/ui/attachmentLogic.ts`). Two copies
 * drift: a type added on one side would make the composer's notice disagree with
 * what the model actually gets. Dependency-free, so both sides can import it.
 */

/** The most of an image or PDF the AI is shown, inline or uploaded (raw bytes,
 *  before base64). Equal to the composer's per-file inline limit on purpose, so
 *  "would the composer have inlined this" and "will the model see it" agree.
 *  Also `read_file`'s vision-attach cap (`features/agent/tools/read-file.ts`):
 *  raising this raises what that tool base64-inlines into a tool result too —
 *  check the providers' per-image limits first. */
export const MAX_MODEL_MEDIA_BYTES = 10 * 1024 * 1024

/** The most of a text attachment folded into the message for the AI. Larger
 *  text reaches the model only as a note saying it was too large to include. */
export const MAX_MODEL_TEXT_BYTES = 256 * 1024

/** The most attached text folded into ONE message for the AI, all its text files
 *  together, in order (Codex review of #1342). Per-file limits alone let forty
 *  256 KB files — each under its own limit, together within the send's 10 MB —
 *  put 10 MB of text (millions of tokens) into one prompt, past any model's
 *  context. A file that would pass it reaches the model as the same "too large to
 *  include" note as an oversized file. Equal to the per-file limit on purpose: one
 *  number, and a single file could already bring that much. */
export const MAX_MODEL_TEXT_PER_MESSAGE_BYTES = MAX_MODEL_TEXT_BYTES

/** The attachment types the AI receives as TEXT (folded into the message, never
 *  a file part — providers take only images and PDFs as files). `image/svg+xml`
 *  is media, not text. */
export function isModelTextMime(mime: string | undefined | null): boolean {
  if (!mime) return false
  return mime.startsWith('text/') || mime === 'application/json' || mime === 'application/xml'
}

/**
 * How long an `attached` CAS row is left alone before the orphan sweep may
 * reclaim it — and, for the same reason, how long the "unattributed"
 * cleanup must not treat one as abandoned.
 *
 * ── Why it lives here and not beside the sweep ──────────────────────────
 *
 * Two places need the SAME window and they are in different layers:
 * `apps/worker`'s sweep, which reclaims unreferenced rows past it, and the
 * chat-storage cleanup planner in `features/agent`, which must not SELECT a
 * conversation because of a row still inside it. `features` cannot import
 * from `apps`, so a constant in the sweep would have meant a second copy —
 * and the two drifting apart is how a transient row becomes a reason to
 * delete somebody's conversation.
 *
 * ── Why a window at all, for the planner ────────────────────────────────
 *
 * Moving an attachment to the Drive writes a CAS copy and drops the old
 * row only afterwards, so for a moment the conversation holds an extra row
 * with NO uploader — indistinguishable, by its columns, from a genuinely
 * unattributable upload (its `state` is `attached`, the same as a real
 * one's). The sweep tells them apart by asking whether any live part still
 * references it, which costs a read of the workspace's SQLite; the planner
 * runs one membership query over Postgres and cannot.
 *
 * The window is a sound proxy THERE specifically because the cleanup only
 * ever looks at conversations idle for weeks: nothing writes an attachment
 * row to one of those except a move in flight. The same reasoning is
 * already relied on by the chat-storage recount, which leaves alone "an
 * upload attached in the last 24 h".
 *
 * `PAPAN_ATTACHED_SESSION_ATTACHMENT_GRACE_MS` overrides it for tests; the
 * sweep reads that env var and this is its default.
 */
export const ATTACHED_ATTACHMENT_GRACE_MS = 24 * 60 * 60 * 1000
